import { getPropertyData } from '@vb-django/api/services/property';

import type { AlertDialogContent, ConfirmProductSelectionFn, Product } from '../../Checkout.types';

import { SearchPropertyDataResponse } from './Subscription.types';

function mapApiToSubscriptionProduct(
  property: SearchPropertyDataResponse,
  designation: string
): Product {
  return {
    type: property.type as Product['type'],
    name: designation,
    id: property.lmID,
    description: `${Math.round(property.forest_land)} hektar skogsmark`,
    price: property.plus_price ?? 0,
    forestArea: Math.round(property.forest_land),
    hasBadData: property.has_bad_data,
    lmID: property.lmID,
    dfr: property.dfr ?? false,
    isOwned: true,
    plus_price_error: property.plus_price_error ?? 0,
    plus_price_max: property.plus_price_max ?? 0
  };
}

export const confirmSubscriptionProductSelection: ConfirmProductSelectionFn = async ({
  pendingSelected,
  previousSelected,
  userProducts
}) => {
  const enrichOwned = (p: Product): Product => {
    const fromList = userProducts.find((u) => u.id === p.id);
    if (fromList) {
      return fromList;
    }
    const fromPrev = previousSelected.find((u) => u.id === p.id);
    return fromPrev ?? p;
  };

  const searchedRefs = pendingSelected.filter((p) => p.isOwned === false).map((p) => p.id);

  const alerts: AlertDialogContent[] = [];

  const validatedByLmId = new Map<string, SearchPropertyDataResponse>();

  if (searchedRefs.length > 0) {
    try {
      const result = (await getPropertyData(searchedRefs)) as {
        ok?: boolean;
        data?: { properties?: SearchPropertyDataResponse[] };
      };
      const properties = result.data?.properties ?? [];

      const getDesignation = (lmID: string) => {
        const row = pendingSelected.find((x) => x.id === lmID);
        return (row?.name as string | undefined) ?? '';
      };

      const toSmallProperties = properties.filter((property) => property.to_small);
      const bigProperties = properties.filter((property) => property.prop_big_area);
      const otherProperties = properties.filter(
        (property) => !property.to_small && !property.prop_big_area
      );

      if (toSmallProperties.length > 0) {
        alerts.push({
          title: 'En eller flera fastigheter är för små',
          subtitle:
            'Följande fastiget(er) kommer inte att kunna läggas till då de är mindre än den minsta storlek som krävs för att köpa plus',
          designation: toSmallProperties.map((p) => getDesignation(p.lmID)).join(', '),
          helperInfo: 'Vänligen välj en annan fastighet - eller kontakta oss för rådgivning.'
        });
      }

      if (bigProperties.length > 0) {
        alerts.push({
          title: 'En eller flera fastigheter är för stor',
          subtitle:
            'Valda fastigheten är större än den största storlek som krävs för att köpa plus',
          designation: bigProperties.map((p) => getDesignation(p.lmID)).join(', '),
          helperInfo: 'Vänligen välj en annan fastighet - eller kontakta oss för rådgivning.'
        });
      }

      if (result.ok) {
        for (const prop of otherProperties) {
          validatedByLmId.set(prop.lmID, prop);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  const nextSelected: Product[] = [];
  for (const p of pendingSelected) {
    if (p.isOwned === false) {
      const api = validatedByLmId.get(p.id);
      if (api) {
        const designation = (p.name as string | undefined) ?? '';
        nextSelected.push(mapApiToSubscriptionProduct(api, designation));
      }
      continue;
    }
    nextSelected.push(enrichOwned(p));
  }

  return { nextSelected, alerts: alerts.length > 0 ? alerts : undefined };
};
